import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import api from '../api';

// ── Styles (matches App.js theme) ───────────────────────
const C = {
    bg: '#0a0a0f', card: '#12121a', hover: '#1a1a26', active: '#222233',
    border: '#2a2a3d', borderLight: '#3a3a55',
    text: '#e8e8f0', muted: '#8888a8', dim: '#5a5a78',
    accent: '#6366f1', accentDim: 'rgba(99,102,241,0.15)',
    success: '#22c55e', warning: '#f59e0b', danger: '#ef4444', info: '#06b6d4',
    purple: '#a78bfa',
};

const num = v => Number(v || 0).toLocaleString();
const fmtClock = ts => ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
const isToday = ts => ts && new Date(ts).toDateString() === new Date().toDateString();
const fmtWhen = ts => {
    if (!ts) return 'awaiting slot';
    return isToday(ts) ? fmtClock(ts) : `${new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${fmtClock(ts)}`;
};
const countdown = ts => {
    if (!ts) return '';
    const diff = new Date(ts) - Date.now();
    if (diff <= 0) return 'due now';
    const m = Math.round(diff / 60000);
    if (m < 60) return `in ${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `in ${h}h ${m % 60}m`;
    return `in ${Math.round(h / 24)}d`;
};
const healthColor = v => Number(v || 0) > 90 ? C.success : Number(v || 0) > 75 ? C.warning : C.danger;
const campaignChip = status => ({
    active: { color: C.success, label: 'live' }, scheduled: { color: C.purple, label: 'scheduled' },
    paused: { color: C.warning, label: 'paused' }, completed: { color: C.info, label: 'done' },
}[status] || { color: C.muted, label: status || 'draft' });

// ── 24-hour mini strip: what an inbox sent / will send today ──
function HourStrip({ hours, height = 14 }) {
    const nowH = new Date().getHours();
    return (
        <div style={{ display: 'flex', gap: 1, height }}>
            {Array.from({ length: 24 }, (_, h) => {
                const b = hours?.[h] || {};
                const sent = Number(b.sent || 0), sch = Number(b.scheduled || 0), trouble = Number(b.trouble || 0);
                let bg = C.active;
                if (sent && sch) bg = `linear-gradient(180deg, ${C.accent} 0 50%, ${C.success} 50% 100%)`;
                else if (sent) bg = C.success;
                else if (trouble) bg = C.danger;
                else if (sch) bg = h < nowH ? C.warning : C.accent; // scheduled in a past hour but not sent = behind plan
                const parts = [];
                if (sent) parts.push(`${sent} sent`); if (sch) parts.push(`${sch} scheduled`); if (trouble) parts.push(`${trouble} failed/deferred`);
                return <div key={h} title={`${String(h).padStart(2, '0')}:00 — ${parts.join(', ') || 'quiet'}`}
                    style={{ flex: 1, borderRadius: 2, background: bg, opacity: bg === C.active ? 0.6 : 1,
                        boxShadow: h === nowH ? `inset 0 0 0 1px ${C.text}` : 'none' }} />;
            })}
        </div>
    );
}

// ── Account-wide tape: expected vs actual traffic today ──
function TodayTape({ totals }) {
    const max = Math.max(1, ...totals.map(t => t.sent + t.scheduled));
    const now = new Date();
    const frac = (now.getHours() + now.getMinutes() / 60) / 24;
    const sentTotal = totals.reduce((a, t) => a + t.sent, 0), schTotal = totals.reduce((a, t) => a + t.scheduled, 0);
    return (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <div>
                    <b>Today’s traffic tape</b>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                        {num(sentTotal)} delivered · {num(schTotal)} still expected today — the marker is now.
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 11, color: C.muted, alignItems: 'center' }}>
                    <span><i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: C.success, marginRight: 4 }} />sent</span>
                    <span><i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: C.accent, marginRight: 4 }} />expected</span>
                    <span><i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: C.warning, marginRight: 4 }} />behind plan</span>
                </div>
            </div>
            <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 76, borderBottom: `1px solid ${C.border}`, paddingBottom: 1 }}>
                    {totals.map((t, h) => {
                        const sentH = Math.round((t.sent / max) * 70), schH = Math.round((t.scheduled / max) * 70);
                        const behind = h < now.getHours() && t.scheduled > 0;
                        return <div key={h} title={`${String(h).padStart(2, '0')}:00 — ${t.sent} sent, ${t.scheduled} scheduled`}
                            style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', cursor: 'default' }}>
                            <div style={{ height: Math.max(schH, t.scheduled ? 3 : 0), background: behind ? C.warning : C.accent, opacity: 0.8, borderRadius: '2px 2px 0 0' }} />
                            <div style={{ height: Math.max(sentH, t.sent ? 3 : 0), background: C.success, borderRadius: schH ? 0 : '2px 2px 0 0' }} />
                        </div>;
                    })}
                </div>
                <div style={{ position: 'absolute', top: -6, bottom: 0, left: `${frac * 100}%`, width: 0, borderLeft: `1px dashed ${C.danger}` }}>
                    <span style={{ position: 'absolute', top: -8, left: -3, width: 7, height: 7, borderRadius: '50%', background: C.danger, animation: 'relayPulse 2s infinite' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.dim, marginTop: 5 }}>
                    {['00', '06', '12', '18', '24'].map(l => <span key={l}>{l}:00</span>)}
                </div>
            </div>
        </div>
    );
}

// ── Deep-dive: exact times for one inbox, behind a click ──
function InboxDetail({ inbox, history, onClose }) {
    const [detail, setDetail] = useState(null);
    useEffect(() => {
        let on = true;
        setDetail(null);
        api.getInboxSchedule(inbox.key).then(d => on && setDetail(d)).catch(e => on && setDetail({ error: e.message }));
        return () => { on = false; };
    }, [inbox.key]);
    const days = useMemo(() => {
        const map = Object.fromEntries((history || []).map(h => [new Date(h.day).toDateString(), Number(h.sent)]));
        return Array.from({ length: 14 }, (_, i) => {
            const d = new Date(); d.setDate(d.getDate() - (13 - i));
            return { day: d, sent: map[d.toDateString()] || 0 };
        });
    }, [history]);
    const maxDay = Math.max(1, ...days.map(d => d.sent));
    return (
        <div style={{ background: C.card, border: `1px solid ${C.accent}55`, borderRadius: 12, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start', marginBottom: 14, flexWrap: 'wrap' }}>
                <div>
                    <b>{inbox.email}</b> <span style={{ color: C.muted, fontSize: 12 }}>· {inbox.domain}</span>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>Exact send times — next in line first. Refreshes with the page.</div>
                </div>
                <button onClick={onClose} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, cursor: 'pointer', padding: '6px 12px', fontFamily: 'inherit', fontSize: 12 }}>Close</button>
            </div>
            {!detail && <div style={{ color: C.muted, fontSize: 12, padding: 12 }}>Loading schedule…</div>}
            {detail?.error && <div style={{ color: C.danger, fontSize: 12, padding: 12 }}>{detail.error}</div>}
            {detail && !detail.error && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                    <div>
                        <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Up next · {detail.upcoming.length ? `${detail.upcoming.length} queued` : 'nothing queued'}</div>
                        <div style={{ maxHeight: 300, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 8 }}>
                            {detail.upcoming.map((u, idx) => (
                                <div key={u.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '9px 12px', borderTop: idx ? `1px solid ${C.border}` : 'none', fontSize: 12, background: idx === 0 ? C.accentDim : 'transparent' }}>
                                    <span style={{ fontFamily: 'monospace', color: idx === 0 ? C.accent : C.text, minWidth: 92 }}>{fmtWhen(u.scheduled_at)}</span>
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.recipient || '—'}<br /><small style={{ color: C.muted }}>{u.campaign}</small></span>
                                    <span style={{ color: u.status === 'deferred' ? C.warning : C.muted, fontSize: 11 }}>{idx === 0 && u.scheduled_at ? countdown(u.scheduled_at) : u.status}</span>
                                </div>
                            ))}
                            {!detail.upcoming.length && <div style={{ padding: 12, color: C.muted, fontSize: 12 }}>No upcoming sends assigned to this inbox.</div>}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Sent today · {detail.recent.length}</div>
                        <div style={{ maxHeight: 224, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 8 }}>
                            {detail.recent.map((r, idx) => (
                                <div key={r.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '9px 12px', borderTop: idx ? `1px solid ${C.border}` : 'none', fontSize: 12 }}>
                                    <span style={{ fontFamily: 'monospace', color: r.status === 'sent' ? C.success : C.danger, minWidth: 60 }}>{fmtClock(r.sent_at)}</span>
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.recipient || '—'}<br /><small style={{ color: C.muted }}>{r.campaign}</small></span>
                                    <span style={{ color: r.status === 'sent' ? C.muted : C.danger, fontSize: 11 }}>{r.status === 'sent' ? 'sent' : r.last_error_class || r.status}</span>
                                </div>
                            ))}
                            {!detail.recent.length && <div style={{ padding: 12, color: C.muted, fontSize: 12 }}>Nothing sent from this inbox yet today.</div>}
                        </div>
                        <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '14px 0 6px' }}>Last 14 days</div>
                        <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 42 }}>
                            {days.map((d, i) => (
                                <div key={i} title={`${d.day.toLocaleDateString()} — ${d.sent} sent`}
                                    style={{ flex: 1, height: Math.max(2, Math.round((d.sent / maxDay) * 40)), background: i === 13 ? C.accent : C.success, opacity: d.sent ? 0.9 : 0.25, borderRadius: 2 }} />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Main flow board ─────────────────────────────────────
export default function CampaignFlow({ overview, refreshSignal }) {
    const [data, setData] = useState(null);
    const [sel, setSel] = useState(null); // {type:'campaign'|'inbox', id}
    const [lines, setLines] = useState([]);
    const boardRef = useRef(null);
    const nodeRefs = useRef(new Map());

    const load = useCallback(() => { api.getCampaignMap().then(setData).catch(e => setData({ error: e.message })); }, []);
    useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
    useEffect(() => { if (refreshSignal) load(); }, [refreshSignal, load]);

    const derived = useMemo(() => {
        if (!data || data.error) return null;
        const inboxKey = e => e.inbox_id || 'unassigned';
        const hourlyByInbox = {}, totals = Array.from({ length: 24 }, () => ({ sent: 0, scheduled: 0 }));
        for (const h of data.hourly || []) {
            const k = h.inbox_id || 'unassigned';
            (hourlyByInbox[k] = hourlyByInbox[k] || {})[h.hour] = h;
            totals[h.hour].sent += Number(h.sent); totals[h.hour].scheduled += Number(h.scheduled);
        }
        const historyByInbox = {};
        for (const h of data.history || []) (historyByInbox[h.inbox_id || 'unassigned'] = historyByInbox[h.inbox_id || 'unassigned'] || []).push(h);
        const inboxes = {};
        for (const e of data.edges || []) {
            const k = inboxKey(e);
            const i = inboxes[k] = inboxes[k] || { key: k, id: e.inbox_id, email: e.inbox_email || 'Awaiting inbox assignment', domain: e.domain || 'Unassigned', sent_today: 0, scheduled_today: 0, upcoming_total: 0, next_send: null, campaigns: new Set() };
            i.sent_today += Number(e.sent_today); i.scheduled_today += Number(e.scheduled_today); i.upcoming_total += Number(e.upcoming_total);
            if (e.next_send && (!i.next_send || new Date(e.next_send) < new Date(i.next_send))) i.next_send = e.next_send;
            i.campaigns.add(e.campaign_id);
        }
        for (const i of Object.values(inboxes)) {
            const ov = (overview?.inboxes || []).find(w => w.id === i.id);
            if (ov) Object.assign(i, { health_score: ov.health_score, per_day_cap: ov.per_day_cap, used_today: ov.used_today, ws_status: ov.status });
        }
        const domains = {};
        for (const i of Object.values(inboxes)) (domains[i.domain] = domains[i.domain] || []).push(i);
        const domainList = Object.entries(domains)
            .sort(([a], [b]) => (a === 'Unassigned') - (b === 'Unassigned') || a.localeCompare(b))
            .map(([domain, list]) => ({ domain, inboxes: list.sort((a, b) => (a.email || '').localeCompare(b.email || '')) }));
        const order = { active: 0, scheduled: 1, paused: 2, draft: 3, completed: 4 };
        const campaigns = [...(data.campaigns || [])].sort((a, b) =>
            (order[a.status] ?? 5) - (order[b.status] ?? 5) || new Date(a.next_send || '2100-01-01') - new Date(b.next_send || '2100-01-01'));
        return { hourlyByInbox, historyByInbox, totals, domainList, campaigns, inboxes };
    }, [data, overview]);

    const setNodeRef = key => el => { el ? nodeRefs.current.set(key, el) : nodeRefs.current.delete(key); };

    const measure = useCallback(() => {
        const board = boardRef.current;
        if (!board || !data?.edges) return setLines([]);
        const b = board.getBoundingClientRect();
        setLines((data.edges || []).map(e => {
            const cEl = nodeRefs.current.get(`c:${e.campaign_id}`), iEl = nodeRefs.current.get(`i:${e.inbox_id || 'unassigned'}`);
            if (!cEl || !iEl) return null;
            const cr = cEl.getBoundingClientRect(), ir = iEl.getBoundingClientRect();
            return { edge: e, x1: cr.right - b.left, y1: cr.top + cr.height / 2 - b.top, x2: ir.left - b.left, y2: ir.top + ir.height / 2 - b.top };
        }).filter(Boolean));
    }, [data]);
    useLayoutEffect(measure, [measure, derived, sel]);
    useEffect(() => { window.addEventListener('resize', measure); return () => window.removeEventListener('resize', measure); }, [measure]);

    if (!data) return <div style={{ color: C.muted, padding: 30 }}>Mapping campaign traffic…</div>;
    if (data.error) return <div style={{ color: C.danger, padding: 30 }}>{data.error}</div>;
    if (!derived?.campaigns.length) return (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 40, textAlign: 'center', color: C.muted }}>
            No live or scheduled campaigns yet. Launch a campaign and its traffic will appear here as a flow map.
        </div>
    );

    const involved = e => sel && (sel.type === 'campaign' ? e.campaign_id === sel.id : (e.inbox_id || 'unassigned') === sel.id);
    const campaignStatus = id => derived.campaigns.find(c => c.id === id)?.status;
    const selCampaign = sel?.type === 'campaign' ? derived.campaigns.find(c => c.id === sel.id) : null;
    const selInbox = sel?.type === 'inbox' ? derived.inboxes[sel.id] : null;
    const maxVol = Math.max(1, ...(data.edges || []).map(e => Number(e.sent_today) + Number(e.scheduled_today) || Number(e.upcoming_total)));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <TodayTape totals={derived.totals} />

            <div ref={boardRef} style={{ position: 'relative' }}>
                <svg className="flow-svg" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', zIndex: 0 }}>
                    {lines.map(({ edge: e, x1, y1, x2, y2 }, idx) => {
                        const vol = Number(e.sent_today) + Number(e.scheduled_today) || Number(e.upcoming_total) || Number(e.sent_total);
                        const status = campaignStatus(e.campaign_id);
                        const live = status === 'active' && Number(e.upcoming_total) > 0;
                        const stroke = live ? C.accent : Number(e.upcoming_total) > 0 ? C.purple : C.success;
                        const opacity = sel ? (involved(e) ? 0.95 : 0.08) : 0.2 + Math.min(0.55, (vol / maxVol) * 0.55);
                        const dx = Math.max(40, Math.min(90, (x2 - x1) / 2));
                        return <path key={idx} d={`M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`}
                            fill="none" stroke={stroke} strokeOpacity={opacity}
                            strokeWidth={1.5 + Math.min(7, Math.sqrt(vol || 1))} strokeLinecap="round"
                            strokeDasharray={live ? '5 9' : 'none'}
                            style={live ? { animation: 'relayFlow 1.4s linear infinite' } : undefined} />;
                    })}
                </svg>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(230px, 0.9fr) 70px minmax(320px, 1.3fr)', alignItems: 'start', position: 'relative', zIndex: 1 }}>
                    {/* Campaigns column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Campaigns · what is being sent</div>
                        {derived.campaigns.map(c => {
                            const chip = campaignChip(c.status);
                            const total = Number(c.total) || 0, sent = Number(c.sent) || 0, remaining = Number(c.remaining) || 0;
                            const dimmed = sel && !(sel.type === 'campaign' ? sel.id === c.id : (data.edges || []).some(e => involved(e) && e.campaign_id === c.id));
                            return (
                                <button key={c.id} ref={setNodeRef(`c:${c.id}`)}
                                    onClick={() => setSel(sel?.type === 'campaign' && sel.id === c.id ? null : { type: 'campaign', id: c.id })}
                                    style={{ textAlign: 'left', background: C.card, border: `1px solid ${sel?.type === 'campaign' && sel.id === c.id ? C.accent : C.border}`,
                                        borderRadius: 10, padding: '12px 14px', cursor: 'pointer', color: C.text, fontFamily: 'inherit',
                                        opacity: dimmed ? 0.35 : 1, transition: 'opacity .15s, border-color .15s' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                                        <b style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</b>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700, color: chip.color, whiteSpace: 'nowrap' }}>
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: chip.color, animation: c.status === 'active' ? 'relayPulse 2s infinite' : 'none' }} />{chip.label}
                                        </span>
                                    </div>
                                    <div style={{ height: 5, background: C.active, borderRadius: 4, overflow: 'hidden', display: 'flex', margin: '9px 0 7px' }}>
                                        <div style={{ width: `${total ? (sent / total) * 100 : 0}%`, background: C.success }} />
                                        <div style={{ width: `${total ? (remaining / total) * 100 : 0}%`, background: C.accent, opacity: 0.55 }} />
                                    </div>
                                    <div style={{ fontSize: 11, color: C.muted, display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                                        <span>{num(sent)}/{num(total)} sent · <span style={{ color: C.text }}>{num(c.sent_today)} today</span></span>
                                        <span style={{ color: c.next_send ? C.accent : C.dim, fontFamily: 'monospace' }}>{c.next_send ? `next ${fmtWhen(c.next_send)}` : remaining ? 'queued' : 'complete'}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="flow-gutter" />

                    {/* Domains → inboxes column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Domains & inboxes · where it leaves from</div>
                        {derived.domainList.map(d => (
                            <div key={d.domain} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                                <div style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline', borderBottom: `1px solid ${C.border}`, background: C.hover }}>
                                    <b style={{ fontSize: 13 }}>{d.domain}</b>
                                    <span style={{ fontSize: 11, color: C.muted }}>
                                        {num(d.inboxes.reduce((a, i) => a + i.sent_today, 0))} sent · {num(d.inboxes.reduce((a, i) => a + i.scheduled_today, 0))} to go today
                                    </span>
                                </div>
                                {d.inboxes.map(i => {
                                    const selected = sel?.type === 'inbox' && sel.id === i.key;
                                    const dimmed = sel && !selected && !(data.edges || []).some(e => involved(e) && (e.inbox_id || 'unassigned') === i.key);
                                    const cap = Number(i.per_day_cap) || 0, used = Number(i.used_today) || 0;
                                    const loadPct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
                                    return (
                                        <button key={i.key} ref={setNodeRef(`i:${i.key}`)}
                                            onClick={() => setSel(selected ? null : { type: 'inbox', id: i.key })}
                                            style={{ width: '100%', textAlign: 'left', background: selected ? C.accentDim : 'transparent', border: 'none',
                                                borderTop: `1px solid ${C.border}`, padding: '10px 14px', cursor: 'pointer', color: C.text, fontFamily: 'inherit',
                                                opacity: dimmed ? 0.35 : 1, transition: 'opacity .15s', display: 'flex', flexDirection: 'column', gap: 7 }}>
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                {i.id && <span title={`health ${i.health_score || 0}/100`} style={{ width: 8, height: 8, borderRadius: '50%', background: healthColor(i.health_score), flexShrink: 0 }} />}
                                                <b style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.email}</b>
                                                <span style={{ fontSize: 11, color: i.next_send ? C.accent : C.dim, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                    {i.next_send ? `${fmtWhen(i.next_send)} · ${countdown(i.next_send)}` : i.upcoming_total ? 'awaiting slot' : 'idle'}
                                                </span>
                                            </div>
                                            <HourStrip hours={derived.hourlyByInbox[i.key]} />
                                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 11, color: C.muted }}>
                                                <span style={{ whiteSpace: 'nowrap' }}>{num(i.sent_today)} sent · {num(i.scheduled_today)} queued today</span>
                                                {cap > 0 && <>
                                                    <div style={{ flex: 1, height: 4, background: C.active, borderRadius: 4, overflow: 'hidden' }}>
                                                        <div style={{ width: `${loadPct}%`, height: '100%', background: loadPct > 90 ? C.danger : loadPct > 70 ? C.warning : C.success }} />
                                                    </div>
                                                    <span style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{used}/{cap}</span>
                                                </>}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div style={{ fontSize: 11, color: C.dim }}>
                Click a campaign or inbox to isolate its traffic; click an inbox for exact send times. Animated lines are actively sending.
            </div>

            {selInbox && <InboxDetail inbox={selInbox} history={derived.historyByInbox[selInbox.key]} onClose={() => setSel(null)} />}

            {selCampaign && (
                <div style={{ background: C.card, border: `1px solid ${C.accent}55`, borderRadius: 12, padding: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start', marginBottom: 12, flexWrap: 'wrap' }}>
                        <div>
                            <b>{selCampaign.name}</b> <span style={{ color: campaignChip(selCampaign.status).color, fontSize: 11, fontWeight: 700 }}>{campaignChip(selCampaign.status).label}</span>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                                {num(selCampaign.sent)} of {num(selCampaign.total)} sent · {num(selCampaign.remaining)} remaining
                                {selCampaign.estimated_completion ? ` · plan finishes ${fmtWhen(selCampaign.estimated_completion)}` : ''}
                            </div>
                        </div>
                        <button onClick={() => setSel(null)} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, cursor: 'pointer', padding: '6px 12px', fontFamily: 'inherit', fontSize: 12 }}>Close</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px,1.4fr) 110px 110px 110px 150px', gap: 10, fontSize: 10, color: C.muted, textTransform: 'uppercase', padding: '6px 0' }}>
                        <span>Sends from</span><span>Sent today</span><span>Queued today</span><span>Remaining</span><span>Next send</span>
                    </div>
                    {(data.edges || []).filter(e => e.campaign_id === selCampaign.id).map((e, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px,1.4fr) 110px 110px 110px 150px', gap: 10, padding: '10px 0', borderTop: `1px solid ${C.border}`, fontSize: 12, alignItems: 'center' }}>
                            <span><b>{e.inbox_email || 'Awaiting assignment'}</b><br /><small style={{ color: C.muted }}>{e.domain}</small></span>
                            <span style={{ color: C.success }}>{num(e.sent_today)}</span>
                            <span>{num(e.scheduled_today)}</span>
                            <span>{num(e.upcoming_total)}</span>
                            <span style={{ fontFamily: 'monospace', color: e.next_send ? C.accent : C.dim }}>{e.next_send ? fmtWhen(e.next_send) : '—'}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
